import jsYaml from "js-yaml";
import stringify from "json-stable-stringify";
import { uniqueNamesGenerator, adjectives, colors, animals } from "unique-names-generator";
import { DEPLOYMENT_ENV, HUBSPOT_SERVICE_URL, IS_HUBSPOT_INTEGRATION_ENABLED } from "../envVariables";
import { linksOf, request, resourceLink } from "./cpln";
import { parse_host as parseDomain } from "tld-extract";
import { Audit } from "../pages/auditTrail/types";
import { ContainerStatus } from "../schema/types/containerstatus";
import { DomainStatus } from "../schema/types/domain/entity";
import { Workload } from "../schema/types/workload/entity";
import { store } from "../store";
import { selectIsCplnTeam, selectIsLoggedIn } from "../store/userSlice";
import { Kind } from "../schema/types/base";
import { confirmSecretReveal } from "../utils/confirmSecretReveal";

export function objectToArray(obj: any): string[][] {
  if (!obj) return [];
  const entries = Object.entries(obj);
  for (let entry of entries) {
    if (entry[1] === null || entry[1] === undefined) {
      entry[1] = "";
    } else {
      entry[1] = (entry[1] as any).toString();
    }
  }
  return entries as string[][];
}

interface TagEntry {
  value: string[];
  width: number;
}

export function itemTagsForTable(tagsArray: string[][], columnWidth: number) {
  let tagEntries: TagEntry[] = tagsArray.map((value) => ({
    value,
    width: Math.min(40 + (value[0].length + value[1].length) * 7, 170),
  }));

  let tagEntriesToShow: TagEntry[] = [];

  let defaultWidth = 0;
  function getTagsWidth() {
    let w = 0;
    for (let entry of tagEntriesToShow) {
      w += entry.width;
    }
    return w + defaultWidth;
  }

  let tagIndex = 0;
  while (tagEntriesToShow.length < tagsArray.length && getTagsWidth() < columnWidth) {
    const tagEntry = tagEntries[tagIndex];
    tagEntriesToShow.push(tagEntry);
    tagIndex++;
  }

  if (tagEntriesToShow.length < tagsArray.length) {
    defaultWidth = 25;
  }

  while (getTagsWidth() >= columnWidth - 50) {
    tagEntriesToShow.pop();
  }

  return tagEntriesToShow.length;
}

export function randomName() {
  const length = 8;
  const first = "xbgjkmqacpez";
  const prefix = first[Math.floor(Math.random() * first.length)];
  const remaining = length - 1;

  // Crockford base32 alphabet (lowercase) - excludes I, L, O, U to avoid confusion
  const alphabet = "0123456789abcdefghjkmnpqrstvwxyz";
  const bytes = new Uint8Array(remaining);
  crypto.getRandomValues(bytes);

  let result = prefix;
  for (let i = 0; i < remaining; i++) {
    result += alphabet[bytes[i] % alphabet.length];
  }
  return result;
}

export function randomReadableName(separator: string = "-"): string {
  return uniqueNamesGenerator({
    dictionaries: [adjectives, colors, animals],
    separator,
    length: 3,
    style: "lowerCase",
  });
}

export function getMetricsUrl(
  _type: "sider" | "org" | "workload" | "gvc" | "mk8s",
  metadata: {
    workloadName?: string;
    gvcName?: string;
    clusterName?: string;
    clusterAlias?: string;
  } = {},
) {
  const state = store.getState();
  const gvc = metadata.gvcName || state.context.gvc;
  const { workloadName, clusterName } = metadata;

  let queryParams = "";
  switch (_type) {
    case "gvc":
      queryParams = encodeURI(`var-gvc=${gvc}`);
      return `/d/cpln-metrics-overview/metrics-overview?${queryParams}`;
    case "workload":
      queryParams = encodeURI(`var-gvc=${gvc}&var-workload=${workloadName}`);
      return `/d/cpln-metrics-overview/metrics-overview?${queryParams}`;
    case "mk8s":
      queryParams = encodeURI(`var-cluster=${clusterName}`);
      return `/d/k8s_overview/managed-kubernetes-overview?${queryParams}`;
    case "sider":
    case "org":
    default:
      return "/d/cpln-metrics-overview/metrics-overview-cloud";
  }
}

export function getTracesUrl(
  _type: "org" | "gvc" | "workload",
  metadata: { workloadName?: string; gvcName?: string } = {},
) {
  const params = new URLSearchParams({
    from: "now-1h",
    to: "now",
    "var-metric": "duration",
    actionView: "structure",
  });

  if (["gvc", "workload"].includes(_type) && metadata.gvcName) {
    params.append("var-filters", `resource.gvc|=|${metadata.gvcName}`);
  }
  if (_type === "workload" && metadata.workloadName) {
    params.append("var-filters", `resource.workload|=|${metadata.workloadName}`);
  }

  return `/a/grafana-exploretraces-app/explore?cplnDatasource=tracing&${params.toString()}`;
}

export interface HistoryState {
  from: string;
}

/* istanbul ignore next */
export function execOnEnterKey(e: React.KeyboardEvent) {
  if (e.keyCode !== 13) return () => {};
  e.stopPropagation();
  return function (fn: any) {
    fn(e);
  };
}

/* istanbul ignore next */
export function execOnEnterSpaceKeys(e: React.KeyboardEvent) {
  // enter / space key
  if (e.keyCode !== 13 && e.keyCode !== 32) return () => {};
  e.stopPropagation();
  return function (fn: any) {
    fn(e);
  };
}

export function deploymentContainerSort(workload: Workload) {
  return (a: ContainerStatus, b: ContainerStatus) => {
    const specContainers = workload.spec.containers.map((c) => c.name);
    const first = a.name!;
    const second = b.name!;
    if (!specContainers.includes(first) && !specContainers.includes(second)) {
      return first > second ? 1 : -1;
    }
    if (!specContainers.includes(first)) {
      return 1;
    }
    if (!specContainers.includes(second)) {
      return -1;
    }

    const firstIndex = specContainers.indexOf(first);
    const secondIndex = specContainers.indexOf(second);
    return firstIndex - secondIndex;
  };
}

export function k8sKeySort(a: string, b: string): number {
  const firstKeys = [
    //
    "kind",
    "itemKind",
    "name",
    "description",
    "tags",
    "origin",
    "type",
    "provider",
    "data",
  ];

  const lastKeys = [
    //
    "version",
    "id",
    "spec",
    "status",
    "created",
    "lastModified",
    "manifest",
    "links",
  ];

  if (a == b) {
    return 0;
  }

  const fa = firstKeys.indexOf(a);
  const fb = firstKeys.indexOf(b);

  // 2: both at the top
  if (fa >= 0 && fb >= 0) {
    return fa - fb;
  }

  const la = lastKeys.indexOf(a);
  const lb = lastKeys.indexOf(b);

  // 1: both at the bottom
  if (la >= 0 && lb >= 0) {
    return la - lb;
  }

  // 4: none is first
  if (fa < 0 && fb < 0 && la < 0 && lb < 0) {
    // natural compare in the middle
    return a < b ? -1 : 1;
  }

  // 6: one shoud go to top
  if (fb >= 0) {
    // b is first
    return 1;
  }
  if (fa >= 0) {
    // b is first
    return -1;
  }

  // 5: at the end
  if (lb >= 0) {
    return -1;
  }
  if (la >= 0) {
    return 1;
  }

  return 0;
}

export function toSortedJSONString(obj: any): string {
  return (
    stringify(obj, {
      cmp: (a: any, b: any) => k8sKeySort(a.key, b.key),
    }) || ""
  );
}

export function toSortedJSON(obj: any): any {
  return JSON.parse(toSortedJSONString(obj));
}

export async function updateLastDeploymentTimeOnHubspot() {
  if (!IS_HUBSPOT_INTEGRATION_ENABLED) {
    return;
  }
  const isLoggedIn = selectIsLoggedIn(store.getState());
  const isCplnTeam = selectIsCplnTeam(store.getState());
  if (!isLoggedIn || isCplnTeam) {
    return;
  }
  try {
    const state = store.getState();
    const org = state.context.org;
    const time = new Date().toISOString().slice(0, 10);
    await request({
      service: "self",
      method: "post",
      url: `${HUBSPOT_SERVICE_URL}/lastdeployment`,
      body: { environment: DEPLOYMENT_ENV, time, org },
    });
  } catch (e) {
    console.error("Failed to update last deployment date");
  }
}

export function isApex(domain: string) {
  if (!domain) {
    return false;
  }
  try {
    const res = parseDomain(domain);
    if (res.sub === "") {
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
}

export function parsedDomain(domain: string): { tld: string; domain: string; sub: string } {
  const defaultObj = { tld: "", domain: "", sub: "" };
  if (!domain) {
    return defaultObj;
  }
  try {
    const res = parseDomain(domain);
    return res;
  } catch (e) {
    return defaultObj;
  }
}

export function clearItem(item: any) {
  delete item.alias;
  delete item.version;
  delete item.id;
  delete item.links;
  delete item.created;
  delete item.lastModified;
  delete item.status;
  delete item.origin;
  delete item.meta;
  delete item.health;

  if (item.tags) {
    delete item.tags["cpln/deployTimestamp"];
  }
}

// Copied from nodelibs/schema/src/workload.ts
export function convertCpuStringToNumber(cpuString: string | undefined): number {
  let cpu = 0;

  if (cpuString) {
    let m = cpuString.split("m");
    if (m.length == 2) {
      cpu = cpu + parseInt(m[0]);
    }
    if (m.length == 1) {
      cpu = cpu + parseFloat(m[0]) * 1000;
    }
  }

  return cpu;
}

// Copied from nodelibs/schema/src/workload.ts
export function convertMemoryStringToNumber(memory: string | undefined): number {
  // returns values in Mib
  let mem = 0;
  if (!memory) {
    return mem;
  }

  let memArray = memory.split(/(?=[GMKgmk])/);
  if (memArray.length == 2) {
    switch (memArray[1].toLowerCase()) {
      case "ki":
        mem = mem + Math.round(parseInt(memArray[0]) / 1024);
        break;
      case "k":
        mem = mem + Math.round(parseInt(memArray[0]) / 1048.576);
        break;
      case "mi":
        mem = mem + parseInt(memArray[0]);
        break;
      case "m":
        mem = mem + Math.round(parseInt(memArray[0]) / 1.048576);
        break;
      case "gi":
        mem = mem + parseFloat(memArray[0]) * 1024.0;
        break;
      case "g":
        mem = mem + Math.round(parseFloat(memArray[0]) * 954);
        break;
    }
  } else {
    mem = Math.round(parseInt(memArray[0]) / 1048576);
  }
  return mem;
}

export function nameOfDomainStatus(status: DomainStatus) {
  const s = status?.status;
  if (!s) {
    return "Not Available";
  }
  switch (s) {
    case "initializing":
      return "Initializing";
    case "ready":
      return "Ready";
    case "pendingDnsConfig":
      return "Pending DNS Config";
    case "pendingCertificate":
      return "Pending Certificate";
    case "usedByGvc":
      return "Used By GVC";
    case "warning":
      return "Warning";
    default:
      return s;
  }
}

export function getErrorMessage(e: any): string {
  let errorMessage = e?.response?.data?.message;
  if (!errorMessage) errorMessage = e?.message || "Error";
  return errorMessage;
}

export function getBillingErrorMessage(e: any): string {
  // Check .error first (billing-ng format)
  if (e?.response?.data?.error) {
    return e.response.data.error;
  }
  // Check .message second
  if (e?.response?.data?.message) {
    return e.response.data.message;
  }
  // Fallback to e.message, with JSON.parse attempt for nested messages
  let errorMessage = e?.message || "Error";
  try {
    const asObject = JSON.parse(errorMessage);
    if (asObject.error) {
      errorMessage = asObject.error;
    } else if (asObject.message) {
      errorMessage = asObject.message;
    }
  } catch (_) {
    // Not JSON, keep original errorMessage
  }
  return errorMessage;
}

export function getReferralCookies(): { pId: string; pVisitorId: string; visitorId: string } {
  return {
    pId: getReferralCookie("pId"),
    pVisitorId: getReferralCookie("pVisitorId"),
    visitorId: getReferralCookie("visitorId"),
  };
}

export function getReferralCookie(cookieName: string) {
  let cookies = document.cookie.split(";");
  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i];
    while (cookie.charAt(0) == " ") {
      cookie = cookie.substring(1);
    }
    if (cookie.indexOf(cookieName) == 0) {
      return decodeURIComponent(cookie.slice(cookieName.length + 1));
    }
  }
  return "";
}

export const queryProperties = {
  agent: ["name", "id", "version", "description", "created", "lastModified"],
  auditctx: ["name", "id", "version", "description", "created", "lastModified"],
  cloudaccount: ["name", "id", "version", "description", "created", "lastModified", "provider"],
  domain: ["name", "id", "version", "description", "created", "lastModified", "status.status"],
  group: ["name", "id", "version", "description", "created", "lastModified", "origin"],
  gvc: ["name", "id", "version", "description", "created", "lastModified", "alias"],
  identity: ["name", "id", "version", "description", "created", "lastModified"],
  image: ["name", "id", "digest", "repository", "tag"],
  ipset: ["name", "id", "version", "description", "created", "lastModified"],
  location: ["name", "id", "version", "description", "created", "lastModified", "origin", "provider", "region", "spec.enabled"],
  mk8s: ["name", "id", "version", "description", "created", "lastModified", "alias", "spec.provider"],
  org: ["name", "id", "version", "description", "created", "lastModified", "subscription"],
  policy: ["name", "id", "version", "description", "created", "lastModified", "targetKind", "target", "origin"],
  quota: ["name", "max", "current", "description", "version", "created", "lastModified", "id", "unit", "origin"],
  secret: ["name", "id", "version", "description", "created", "lastModified", "type"],
  serviceaccount: ["name", "id", "version", "description", "created", "lastModified", "origin"],
  task: ["created", "lastModified", "targetEmail", "creatorLink", "type", "id", "status"],
  user: ["idp", "email", "name", "id", "version", "created", "lastModified"],
  volumeset: ["name", "id", "version", "description", "created", "lastModified"],
  workload: ["name", "id", "version", "description", "created", "lastModified", "spec.type", "health.readiness", "health.syncFailed", "status.suspendedStatus"],
};

export interface DryRunSchemaErrorDetail {
  context?: { key?: string; label?: string; valids?: string[]; value?: string };
  message?: string;
  path?: string[];
  type?: string;
}

export interface DryRunSchemaError {
  id: string;
  message: string;
  status: number;
  details: DryRunSchemaErrorDetail[];
}

export interface DryRunResponse {
  code: string;
  id: string;
  message: string;
  status: number;
  details: {
    issues: DryRunIssue[];
  };
}

export interface DryRunIssue {
  message: string;
  recommendation?: string | string[];
  path: string;
  severity: string;
}

export function stringPairsToObject(arr: string[], separator = "=") {
  let obj: { [_: string]: string } = {};
  for (const item of arr) {
    const key = item.split(separator)[0];
    const value = item.split(separator)[1];
    obj[key] = value;
  }
  return obj;
}

export const OPERATORS_REQUIRING_TWO_VALUES = ["=", "<", ">", "<=", ">=", "!=", "~", "=~"];

export function isAuditDiffable(audit: Audit) {
  const actionTypesToPreventDiff = ["create", "edit"]; // shell exec reveal
  return audit.action?.type && actionTypesToPreventDiff.includes(audit.action.type);
}

export const tagLinkUrlPrefixes = ["https://", "http://", "ws://", "wss://"];

export function parseEnvFile(src: string) {
  // Only match KEY=value format (not KEY: value which is YAML)
  const LINE = /(?:^|^)\s*(?:export\s+)?([\w.-]+)\s*=\s*?(\s*'(?:\\'|[^'])*'|\s*"(?:\\"|[^"])*"|\s*`(?:\\`|[^`])*`|[^#\r\n]+)?\s*(?:#.*)?(?:$|$)/gm;
  const obj: { [_: string]: any } = {};

  // Convert buffer to string
  let lines = src.toString();

  // Convert line breaks to same format
  lines = lines.replace(/\r\n?/gm, "\n");

  let match;
  while ((match = LINE.exec(lines)) != null) {
    const key = match[1];

    // Default undefined or null to empty string
    let value = match[2] || "";

    // Remove whitespace
    value = value.trim();

    // Check if double quoted
    const maybeQuote = value[0];

    // Remove surrounding quotes
    value = value.replace(/^(['"`])([\s\S]*)\1$/gm, "$2");

    // Expand newlines if double quoted
    if (maybeQuote === '"') {
      value = value.replace(/\\n/g, "\n");
      value = value.replace(/\\r/g, "\r");
    }

    // Add to object
    obj[key] = value;
  }

  return obj;
}

// TODO make a utils folder and separate functions/types
export interface CronSchedule {
  minuteSegments: CronSegment[];
  hourSegments: CronSegment[];
  dayOfMonthSegments: CronSegment[];
  monthSegments: CronSegment[];
  dayOfWeekSegments: CronSegment[];
}
export interface CronSegment {
  absoluteRangeMin: number;
  absoluteRangeMax: number;
  stepValue: number;
  wildcard: boolean;
}

const volumesetScheduleRegex = /^(\*|(\d+(-\d+)?))(\/\d+)?(,(\*|(\d+(-\d+)?))(\/\d+)?)*(\s(\*|(\d+(-\d+)?))(\/\d+)?(,(\*|(\d+(-\d+)?))(\/\d+)?)*){4}$/;
export function parseCronSchedule(cronSchedule: string): CronSchedule {
  if (!cronSchedule.match(volumesetScheduleRegex)) {
    throw new Error("The given schedule does not match the standard cron format.");
  }

  const pieces = cronSchedule.split(/\s/);

  if (pieces.length != 5) {
    throw new Error("The given schedule does not match the standard cron format.");
  }

  let parsedSchedule: CronSchedule = {
    minuteSegments: [],
    hourSegments: [],
    dayOfMonthSegments: [],
    monthSegments: [],
    dayOfWeekSegments: [],
  };
  parsedSchedule.minuteSegments = parseCronScheduleSegment(pieces[0]);
  parsedSchedule.hourSegments = parseCronScheduleSegment(pieces[1]);
  parsedSchedule.dayOfMonthSegments = parseCronScheduleSegment(pieces[2]);
  parsedSchedule.monthSegments = parseCronScheduleSegment(pieces[3]);
  parsedSchedule.dayOfWeekSegments = parseCronScheduleSegment(pieces[4]);
  return parsedSchedule;
}

function parseCronScheduleSegment(segments: string): CronSegment[] {
  let parsedSegments: CronSegment[] = [];
  for (let s of segments.split(",")) {
    let segment: CronSegment = {
      absoluteRangeMin: 0,
      absoluteRangeMax: 0,
      stepValue: 0,
      wildcard: false,
    };
    let pieces = s.split("/");
    let range = pieces[0].split("-");
    if (pieces[0] == "*") {
      segment.wildcard = true;
    } else {
      segment.absoluteRangeMin = parseInt(range[0]);
      segment.absoluteRangeMax = segment.absoluteRangeMin;
      if (range.length > 1) {
        segment.absoluteRangeMax = parseInt(range[1]);
      }
    }

    if (pieces.length > 1) {
      segment.stepValue = parseInt(pieces[1]);
    }
    parsedSegments.push(segment);
  }
  return parsedSegments;
}

export function stringToHash(value: string) {
  var hash = 0;

  if (value.length == 0) return hash;

  for (let i = 0; i < value.length; i++) {
    let char = value.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }

  return hash;
}

export async function sleep(seconds: number) {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, seconds * 1000);
  });
}

export async function nextTick() {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, 0);
  });
}

export async function getExportData({ kind, selfLink }: { kind: string; selfLink: string }, slim: boolean) {
  if (kind === "account") {
    return;
  }

  let { data: item } = await request({ url: selfLink });

  if (kind === "secret") {
    const shouldReveal = await confirmSecretReveal({ okText: "Reveal & Edit", cancelText: "Edit Without Revealing" });
    if (shouldReveal) {
      try {
        const { data: revealedSecretItem } = await request({ url: selfLink + "/-reveal" });
        item = revealedSecretItem;
      } catch (e) {
        // throw new Error("Secret could not be revealed.");
      }
    }
  }

  if (slim) {
    clearItem(item);
  }
  return item;
}

export function hasDuplicatedValue(items: string[]) {
  const seen = new Set<string>();
  for (const item of items) {
    if (seen.has(item)) {
      return true;
    }
    seen.add(item);
  }
  return false;
}

export function yamlDumpAll(items: any, opts?: jsYaml.DumpOptions): string {
  if (!Array.isArray(items)) {
    return jsYaml.dump(items, opts);
  }

  return items.map((item: any) => jsYaml.dump(item, opts)).join("---\n");
}

export function getIsMarketplaceItem(item: any): boolean {
  return !!item.tags["console/marketplace"];
}

export function getIsHelmReleaseItem(item: any): boolean {
  return !!item.tags["cpln/release"];
}

export const gvcScopedKinds = ["workload", "identity", "volumeset"];
